import React, { useState, useEffect } from 'react';
import {
  X,
  BarChart2,
  Eye,
  MessageSquare,
  TrendingUp,
  Star,
  Users,
  List as ListIcon,
  Lightbulb,
  MapPin,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import api from '../services/api';
import modalsBg from '../assets/Modals.jpg';

const DAYS = 14;

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-400 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-800 leading-tight">{value}</p>
        {sub && <p className="text-xs text-gray-400">{sub}</p>}
      </div>
    </div>
  </div>
);

const BarChart = ({ data, valueKey, color, label }) => {
  const values = data.map((d) => Number(d[valueKey]) || 0);
  const max = Math.max(1, ...values);
  const width = 700;
  const height = 180;
  const barGap = 10;
  const slotWidth = width / data.length;
  const barMaxHeight = 140;
  const labels = data.map((d) =>
    new Date(d.date + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
  );

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height + 24}`} className="w-full h-auto">
        {[0.25, 0.5, 0.75, 1].map((pct) => (
          <line
            key={pct}
            x1="0"
            x2={width}
            y1={height - barMaxHeight * pct}
            y2={height - barMaxHeight * pct}
            stroke="#eef2f7"
            strokeWidth="1"
          />
        ))}
        {data.map((d, i) => {
          const v = Number(d[valueKey]) || 0;
          const barHeight = (v / max) * barMaxHeight;
          const x = i * slotWidth + slotWidth / 2 - 8;
          return (
            <g key={i}>
              <rect
                x={x}
                y={height - barHeight}
                width={16}
                height={Math.max(barHeight, v > 0 ? 2 : 0)}
                rx="4"
                fill={color}
              />
              {v > 0 && (
                <text
                  x={x + 8}
                  y={height - barHeight - 6}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#64748b"
                  fontWeight="600"
                >
                  {v}
                </text>
              )}
              <text
                x={x + 8}
                y={height + 16}
                textAnchor="middle"
                fontSize="10"
                fill="#94a3b8"
              >
                {labels[i]}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="text-xs text-gray-400 mt-1">{label}</p>
    </div>
  );
};

const SkeletonAnalytics = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 animate-pulse">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="h-24 bg-gray-100 rounded-2xl" />
      ))}
    </div>
    <div className="h-56 bg-gray-100 rounded-2xl animate-pulse" />
    <div className="h-40 bg-gray-100 rounded-2xl animate-pulse" />
  </div>
);

const AnalyticsModal = ({ isOpen, onClose, user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeChart, setActiveChart] = useState('views');

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    api
      .getAnalytics()
      .then((res) => setData(res))
      .catch((err) => setError(err.message || 'Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [isOpen]);

  if (!isOpen) return null;

  const tips =
    data && data.totals
      ? [
          ...(data.totals.listings === 0
            ? [{ tone: 'neutral', text: 'You have no listings yet. Add your first property to start building visibility.' }]
            : []),
          ...(data.totals.totalViews === 0 && data.totals.listings > 0
            ? [{ tone: 'danger', text: 'Your listings are not being seen yet. Make sure they are marked as available so they appear in search results.' }]
            : []),
          ...(data.totals.totalViews > 0 && data.totals.totalContacts === 0
            ? [{ tone: 'danger', text: `People are viewing your listings (${data.totals.totalViews} views) but nobody has contacted you. Make sure you've added your phone number and location to your profile so interested people can reach you, then improve your main photo and description.` }]
            : []),
          ...(data.totals.engagementRate > 0 && data.totals.engagementRate < 6
            ? [{ tone: 'warn', text: `Your contact rate is ${data.totals.engagementRate}%. Add more photos and respond to inquiries quickly to convert more views into messages.` }]
            : []),
          ...(data.totals.engagementRate >= 6
            ? [{ tone: 'good', text: `Great job! ${data.totals.engagementRate}% of viewers are reaching out. Keep your listings fresh to maintain this momentum.` }]
            : [])
        ]
      : [];

  const listingTips = (data?.listings || []).filter((l) => Number(l.event_views) > 0 && Number(l.contacts) === 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div
          className="px-6 sm:px-8 py-5 sm:py-6 flex items-center justify-between"
          style={{ backgroundImage: `url(${modalsBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        >
          <div className="flex items-center gap-3">
            <BarChart2 className="w-7 h-7 text-white" />
            <div>
              <h2 className="text-2xl font-bold text-white">Analytics</h2>
              <p className="text-blue-100 text-sm">How your listings are performing</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gradient-to-b from-blue-50/50 to-white">
          {loading ? (
            <SkeletonAnalytics />
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-gray-600">{error}</p>
              <button
                onClick={() => {
                  setLoading(true);
                  api.getAnalytics().then((res) => { setData(res); setError(null); }).catch((e) => setError(e.message)).finally(() => setLoading(false));
                }}
                className="mt-4 px-5 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-600"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Stat cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <StatCard icon={Eye} label="Total Views" value={data.totals.totalViews} accent="bg-blue-50 text-blue-600" />
                <StatCard icon={MessageSquare} label="Contacts" value={data.totals.totalContacts} accent="bg-emerald-50 text-emerald-600" sub={`${data.totals.distinctContactUsers} people`} />
                <StatCard icon={TrendingUp} label="Contact Rate" value={`${data.totals.engagementRate}%`} accent="bg-violet-50 text-violet-600" />
                <StatCard icon={ListIcon} label="Listings" value={data.totals.listings} sub={`${data.totals.activeListings} active`} accent="bg-indigo-50 text-indigo-600" />
                <StatCard icon={Users} label="Active Listings" value={data.totals.activeListings} accent="bg-cyan-50 text-cyan-600" />
                <StatCard icon={Star} label="Owner Rating" value={data.totals.avgRating ?? '—'} sub={`${data.totals.reviewCount} reviews`} accent="bg-amber-50 text-amber-600" />
              </div>

              {/* Chart */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                  <h3 className="text-base font-semibold text-gray-700">Last {DAYS} days</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setActiveChart('views')}
                      className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-colors ${activeChart === 'views' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      Views
                    </button>
                    <button
                      onClick={() => setActiveChart('contacts')}
                      className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-colors ${activeChart === 'contacts' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      Contacts
                    </button>
                  </div>
                </div>
                {activeChart === 'views' ? (
                  <BarChart data={data.viewsTrend} valueKey="views" color="#3b82f6" label="Daily page views across your listings" />
                ) : (
                  <BarChart data={data.contactsTrend} valueKey="contacts" color="#10b981" label="Daily inbound contacts from interested people" />
                )}
              </div>

              {/* Tips */}
              {(tips.length > 0 || listingTips.length > 0) && (
                <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6 shadow-sm">
                  <h3 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-amber-500" />
                    How to improve
                  </h3>
                  <ul className="space-y-2">
                    {tips.map((tip, idx) => (
                      <li
                        key={idx}
                        className={`flex items-start gap-2 rounded-xl px-3 py-2 text-sm ${
                          tip.tone === 'danger'
                            ? 'bg-red-50 text-red-700'
                            : tip.tone === 'warn'
                            ? 'bg-amber-50 text-amber-700'
                            : tip.tone === 'good'
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-gray-50 text-gray-600'
                        }`}
                      >
                        {tip.tone === 'good' ? (
                          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        )}
                        <span>{tip.text}</span>
                      </li>
                    ))}
                    {listingTips.slice(0, 3).map((l, idx) => (
                      <li key={`l-${idx}`} className="flex items-start gap-2 rounded-xl px-3 py-2 text-sm bg-amber-50 text-amber-700">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-semibold">{l.title}</span> gets views but no contacts — confirm your phone number and location are in your profile, and update its photos and description.
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Per-listing table */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6 shadow-sm">
                <h3 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <ListIcon className="w-5 h-5 text-blue-600" />
                  Your listings
                </h3>
                {data.listings.length === 0 ? (
                  <p className="text-sm text-gray-400 py-6 text-center">No listings yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-400 uppercase tracking-wider border-b border-gray-100">
                          <th className="py-2 pr-3 font-medium">Property</th>
                          <th className="py-2 px-3 font-medium">Location</th>
                          <th className="py-2 px-3 font-medium">Price</th>
                          <th className="py-2 px-3 font-medium">Status</th>
                          <th className="py-2 px-3 font-medium">Views</th>
                          <th className="py-2 pl-3 font-medium">Contacts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.listings.map((l) => (
                          <tr key={l.id} className="border-b border-gray-50 last:border-0">
                            <td className="py-3 pr-3">
                              <div className="flex items-center gap-3 min-w-[180px]">
                                {l.images && l.images[0] ? (
                                  <img src={l.images[0]} alt="" className="w-10 h-10 rounded-lg object-cover" />
                                ) : (
                                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                    <ListIcon className="w-4 h-4 text-gray-400" />
                                  </div>
                                )}
                                <span className="font-medium text-gray-700 line-clamp-2">{l.title}</span>
                              </div>
                            </td>
                            <td className="py-3 px-3">
                              <span className="flex items-center gap-1 text-gray-500">
                                <MapPin className="w-3.5 h-3.5 text-blue-500" />
                                {l.city}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-gray-700 font-semibold">
                              GH₵{Number(l.price).toLocaleString()}
                            </td>
                            <td className="py-3 px-3">
                              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${l.is_available ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                                {l.is_available ? (
                                  <>
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Available
                                  </>
                                ) : (
                                  'Unavailable'
                                )}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-gray-700 font-semibold">{Number(l.event_views) || 0}</td>
                            <td className="py-3 pl-3">
                              <span className={`font-semibold ${Number(l.contacts) > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                                {Number(l.contacts) || 0}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsModal;